/**
 * The four lenses on the field — channel flow, NTS, pick-face capacity, customer map —
 * plus the right-hand read-out and the expand modal.
 *
 * Every lens shows the *whole site* by default and overlays the current selection in red, so
 * a marquee on the plot reads straight through to "what would this move cost / earn".
 * Ported imperatively (see the port contract): each renderer writes into the static hosts the
 * field component renders.
 */

import {
  annualisedNote,
  BASIS,
  CHANNELS,
  DEPTH,
  GEO_MARKETS,
  MONTH_SLOTS,
  simulated,
  type Site,
  type Sku,
  YEAR_FACTORS,
} from "@/lib/data";
import { logger } from "@/lib/logger";

import type { ClusterPick } from "./plot";
import {
  curSym,
  el,
  FIELD_YEAR,
  fmtMoneyShort,
  fmtVel,
  moneyM,
  need,
  NPI_TINT,
  ribbon,
  svgText,
} from "./util";

export type WidgetKey = "channel" | "business" | "ops" | "customer";

/** Eyebrow + title per expandable widget. */
export const WL: Record<WidgetKey, [string, string]> = {
  channel: ["Channel", "Product flow"],
  business: ["Business", "Net trade sales"],
  ops: ["Operations", "Pick-face capacity"],
  customer: ["Customer", "Shipped to accounts"],
};

export interface LensCtx {
  skus: readonly Sku[];
  site: Site;
  /** SKUs passing the topbar filters — the "whole site" baseline for every lens. */
  view: readonly Sku[];
  /** Selected SKUs, or `null` when nothing is selected. */
  sel: readonly Sku[] | null;
  chanMode: "channel" | "region";
  /** Σ pick faces across the field — the denominator for a selection's footprint. */
  baseFaces: number;
  /**
   * The cluster glyph the last click landed on, when the selection is still exactly its members.
   * `null` for a marquee, an agent cohort or a single-SKU click — there is no stack to list.
   */
  pick?: ClusterPick | null;
}

const yearFactor = YEAR_FACTORS[FIELD_YEAR] ?? 1;
/**
 * A SKU's monthly boxes, as served — no floor.
 *
 * This was `Math.max(s.vel * yearFactor, 1)`, which invented a box a month for every SKU that
 * shipped nothing. On the real drop the non-mover cohort's served velocity sums to exactly 0, yet
 * the Channel widget read "9,545 boxes/mo" and painted red ribbons across every channel — flow for
 * stock that never moved, contradicting the agent's own answer in the drawer beside it. The
 * divisors are guarded instead, which is where the guard belonged.
 */
const monthlyUnits = (s: Sku) => s.vel * yearFactor;

/* ===== channel / region flow — persistent site structure, red overlay = selection share ===== */

export interface Dest {
  label: string;
  color: string;
  siteV: number;
  selV: number;
}

const REGION_COL = ["#9E0000", "#EB1700", "#C1BBB3", "#81766F", "#D5CFC9", "#564C47", "#A39992"];

// The channel count comes from the data (six real trade channels at UKDC, five in the demo),
// so nothing here may assume a fixed length.
function channelAgg(set: readonly Sku[]) {
  const agg = new Array<number>(CHANNELS.length).fill(0);
  let units = 0;
  for (const s of set) {
    const u = monthlyUnits(s);
    units += u;
    const n = Math.min(s.chan.length, agg.length);
    for (let i = 0; i < n; i++) {
      agg[i] = (agg[i] ?? 0) + (s.chan[i] ?? 0) * u;
    }
  }
  return { agg, units };
}

function regionAgg(set: readonly Sku[]) {
  const m: Record<string, number> = {};
  for (const s of set) {
    const per = monthlyUnits(s) / Math.max(1, s.geo.length);
    for (const g of s.geo) {
      m[g.name] = (m[g.name] ?? 0) + per;
    }
  }
  return m;
}

/**
 * Narrowed to the three `LensCtx` fields this actually reads (not the full context) so the
 * agent-view templates (`flow-view.tsx`) can call it without fabricating a `site`/`skus`/
 * `baseFaces` they don't have — every existing caller passes a full `LensCtx`, which still
 * satisfies this narrower shape structurally, so `renderChannel`/`renderModal` are unaffected.
 */
export function chanDests(ctx: Pick<LensCtx, "view" | "sel" | "chanMode">): Dest[] {
  if (ctx.chanMode === "region") {
    const site = regionAgg(ctx.view);
    const selm = ctx.sel ? regionAgg(ctx.sel) : null;
    const names = Object.keys(site)
      .sort((a, b) => (site[b] ?? 0) - (site[a] ?? 0))
      .slice(0, 7);
    return names.map((n, i) => ({
      label: n,
      color: REGION_COL[i % REGION_COL.length] ?? "#C1BBB3",
      siteV: site[n] ?? 0,
      selV: selm?.[n] ?? 0,
    }));
  }
  const sa = channelAgg(ctx.view).agg;
  const se = ctx.sel ? channelAgg(ctx.sel).agg : null;
  return CHANNELS.map((c, i) => ({
    label: c.label,
    color: c.color,
    siteV: sa[i] ?? 0,
    selV: se ? (se[i] ?? 0) : 0,
  }));
}

export function drawFlow(svg: SVGSVGElement, W: number, H: number, dests: Dest[], hasSel: boolean) {
  svg.setAttribute("viewBox", `0 0 ${W} ${H}`);
  svg.innerHTML = "";
  const tot = dests.reduce((a, d) => a + d.siteV, 0) || 1;
  const nw = Math.max(11, W * 0.045);
  const x1 = W * 0.03;
  const x2 = W * 0.5;
  const top = H * 0.11;
  const usable = H * 0.78;
  const gap = H * 0.03;
  const fs = Math.max(8, W * 0.032);
  const order = dests.slice().sort((a, b) => b.siteV - a.siteV);
  svg.appendChild(el("rect", { x: x1, y: top, width: nw, height: usable, rx: 2, fill: "#141414" }));
  svg.appendChild(svgText({ x: x1, y: top - 5, "font-size": fs, fill: "#81766F" }, "UKDC"));
  const avail = usable - gap * (order.length - 1);
  // One text row here, at `fs` — same collision, same fix as the expanded sankey.
  const labY = labelStack(fs * 1.25);
  let syTop = top;
  let ty = top;
  for (const d of order) {
    const srcH = (d.siteV / tot) * usable;
    const dstH = Math.max(2, (d.siteV / tot) * avail);
    svg.appendChild(
      el("path", {
        class: "sk-link",
        d: ribbon(x1 + nw, syTop, syTop + srcH, x2, ty, ty + dstH),
        fill: d.color,
        "fill-opacity": hasSel ? 0.16 : 0.42,
      }),
    );
    const rS = (d.selV / tot) * usable;
    const rD = (d.selV / tot) * avail;
    if (hasSel && rD > 0.3) {
      svg.appendChild(
        el("path", {
          class: "sk-link",
          d: ribbon(x1 + nw, syTop + srcH - rS, syTop + srcH, x2, ty + dstH - rD, ty + dstH),
          fill: "#EB1700",
          "fill-opacity": 0.85,
        }),
      );
    }
    svg.appendChild(
      el("rect", {
        x: x2,
        y: ty,
        width: nw,
        height: dstH,
        rx: 2,
        fill: d.color,
        "fill-opacity": hasSel ? 0.3 : 0.85,
      }),
    );
    if (hasSel && rD > 0.3) {
      svg.appendChild(
        el("rect", { x: x2, y: ty + dstH - rD, width: nw, height: rD, rx: 2, fill: "#EB1700" }),
      );
    }
    svg.appendChild(
      svgText(
        { x: x2 + nw + 6, y: labY(ty + dstH / 2 + fs * 0.35), "font-size": fs, fill: "#564C47" },
        d.label + " " + Math.round((d.siteV / tot) * 100) + "%",
      ),
    );
    syTop += srcH;
    ty += dstH + gap;
  }
}

export function renderChannel(ctx: LensCtx) {
  const dests = chanDests(ctx);
  const siteUnits = dests.reduce((a, d) => a + d.siteV, 0);
  const selUnits = ctx.sel ? dests.reduce((a, d) => a + d.selV, 0) : 0;
  // A selection can legitimately carry no flow at all (the non-mover cohort is the obvious case),
  // and "0 boxes/mo · 0% of flow" reads like a rendering failure rather than the finding it is.
  need("ch-v").innerHTML = !ctx.sel
    ? Math.round(siteUnits).toLocaleString() + " <small>boxes/mo</small>"
    : selUnits > 0
      ? Math.round(selUnits).toLocaleString() +
        " <small>boxes/mo · " +
        ((selUnits / (siteUnits || 1)) * 100).toFixed(0) +
        "% of flow</small>"
      : "None <small>— no shipments in the window</small>";
  drawFlow(need<SVGSVGElement>("ch-svg"), 260, 120, dests, ctx.sel !== null);
  need("ch-cap").textContent = ctx.sel
    ? ctx.chanMode === "region"
      ? "Red = the selection’s share by market/region — where a move would land."
      : "Red = the selection’s share of each channel."
    : ctx.chanMode === "region"
      ? "Which markets this site’s volume serves."
      : "Where this site’s volume flows to market.";
}

/* ===== NTS — clean bars, full by default, fill = selection share ===== */

/**
 * NTS by fiscal month, over the months the drop actually covers.
 *
 * `Sku.mon` is a twelve-slot calendar mix; the chart is as wide as `MONTH_SLOTS`, so a
 * single-month drop draws one bar for its one month and a twelve-month drop draws twelve —
 * rather than one bar in eleven empty frames, which reads as a broken chart.
 */
export function ntsData(view: readonly Sku[]) {
  const slots = MONTH_SLOTS;
  const mon = new Array<number>(slots.length).fill(0);
  let val = 0;
  for (const s of view) {
    val += s.value;
    slots.forEach((sl, j) => {
      mon[j] = (mon[j] ?? 0) + (s.mon[sl.mon] ?? 0) * s.value;
    });
  }
  return { mon, val };
}

/**
 * Site NTS as the site actually bills it (decision 7): the native amounts on the value line,
 * the converted total on the line beneath. A site billing in one currency shows one figure and
 * no total line. All arithmetic elsewhere uses `site.nts`, which is already normalised.
 */
function ntsRead(site: Site): { top: string; total: string | null } {
  const one = { top: moneyM(site.nts, site.ntsCurrency), total: null };
  const native = site.ntsNative;
  if (!native || native.length < 2) {
    return one;
  }
  // The breakdown is only shown if it adds up to the anchor: two figures on screen that
  // disagree are worse than one. A drop whose breakdown is on a different basis than
  // `site.nts` degrades to the normalised total, and says so in the console.
  const recon = native.reduce((a, n) => a + n.amountReporting, 0);
  if (Math.abs(recon - site.nts) > 0.01 * site.nts) {
    logger.warn("[lenses] site.ntsNative does not reconcile with site.nts", recon, site.nts);
    return one;
  }
  return {
    top: native.map((n) => moneyM(n.amountNative, n.currency)).join(" + "),
    total: `≈ ${moneyM(site.nts, site.ntsCurrency)} total (${site.ntsCurrency ?? "GBP"})`,
  };
}

function drawNTS(host: HTMLElement, siteMon: number[], selMon: number[] | null) {
  const gmax = Math.max(...siteMon) || 1;
  // Keyed on the bar count: the drop's month count is known before the first paint, but a
  // cached twelve-bar frame under a one-month drop is exactly the empty frame being fixed.
  if (host.dataset.built !== String(MONTH_SLOTS.length)) {
    host.innerHTML = MONTH_SLOTS.map(
      (sl) =>
        `<div class="mbar"><div class="mbar-c"><div class="mbar-f"></div></div><div class="lbl">${sl.label}</div></div>`,
    ).join("");
    host.dataset.built = String(MONTH_SLOTS.length);
  }
  const bars = host.querySelectorAll<HTMLElement>(".mbar");
  // A one- or two-month drop centres its bars instead of stretching one across the frame:
  // `.mbar` is `flex:1`, so without this a period total reads as a slab.
  const narrow = MONTH_SLOTS.length < 4;
  host.style.justifyContent = narrow ? "center" : "";
  siteMon.forEach((sv, i) => {
    const bar = bars[i];
    const c = bar?.querySelector<HTMLElement>(".mbar-c");
    const f = bar?.querySelector<HTMLElement>(".mbar-f");
    if (!bar || !c || !f) {
      return;
    }
    bar.style.maxWidth = narrow ? "30%" : "";
    c.style.height = Math.max(6, (sv / gmax) * 100) + "%";
    const frac = selMon ? Math.min(1, (selMon[i] ?? 0) / (sv || 1)) : 1;
    f.style.height = frac * 100 + "%";
    f.style.background = selMon ? "#EB1700" : "#C1BBB3";
  });
}

export function renderBusiness(ctx: LensCtx) {
  const { mon: siteMon, val: siteVal } = ntsData(ctx.view);
  const selected = ctx.sel ? ntsData(ctx.sel) : null;
  drawNTS(need("bz-nts"), siteMon, selected ? selected.mon : null);
  if (selected) {
    need("bz-v").innerHTML =
      fmtMoneyShort(selected.val * (ctx.site.nts / siteVal), ctx.site.ntsCurrency) +
      " <small>· " +
      ((selected.val / siteVal) * 100).toFixed(1) +
      "% of NTS</small>";
    need("bz-cap").textContent = shareCaption();
  } else {
    const nts = ntsRead(ctx.site);
    need("bz-v").innerHTML = nts.top + " <small>/yr</small>";
    // The chart is now exactly as wide as the drop's own window, so the caption states what
    // that window is — a one-bar chart is a period total, not a year with eleven months missing.
    need("bz-cap").textContent =
      (nts.total ? nts.total + " · " : "") + periodCaption(siteMon.length);
  }
}

/**
 * "annualised from …", or nothing on a drop that observed a full year.
 *
 * Every money figure on this screen is the observed window scaled to twelve months, and the only
 * surface saying so used to be `periodCaption` — which the no-selection branch owns, i.e. the
 * disclosure vanished the instant anything was selected. Derived from the drop's own month count,
 * so a twelve-month drop says nothing and does not claim an annualisation it did not make.
 */
function annualisedClause(): string {
  const note = annualisedNote(MONTH_SLOTS.length);
  return note ? ` ${note}` : "";
}

/** The selection read: how the bars fill, plus the annualisation the figures carry. */
function shareCaption(): string {
  const one = MONTH_SLOTS.length === 1;
  return (
    (one
      ? "The bar fills to the selection’s share of the period’s NTS."
      : "Each month fills to the selection’s share of that month’s NTS.") + annualisedClause()
  );
}

/** What the bars cover, derived from the months the drop carries. */
function periodCaption(months: number): string {
  if (months === 1) {
    return `${MONTH_SLOTS[0]?.label ?? BASIS.salesSpan} is the only fiscal month this drop covers — one bar is the whole period, annualised here to a year.`;
  }
  if (months < 12) {
    return `Site NTS across the ${months} fiscal months this drop covers, annualised to a year · select SKUs and the bars drop to their share.`;
  }
  return "Full = site NTS by month · select SKUs and the bars drop to their share.";
}

/* ===== pick-face — clean grid, full by default, fill = selection share =====
 * Two bases meet here and neither may be dressed up as the other: `site.pickCap` and
 * `site.occ` are planning-deck figures (no capacity feed exists in the drop), while a SKU's
 * `faces` is boxes held on a depth scale the build picks for the plot. Σfaces is therefore a
 * FOOTPRINT, not a count of faces, and quoting it as one put a five-figure "faces" total in the
 * same tile as a four-figure ceiling. So the deck figures own the no-selection read, a selection
 * reads as a SHARE of the field's own estimated depth (scale-free — see `DEPTH.rule`), and each
 * branch says which basis it is on. Both provenance clauses are gated on the drop's own
 * provenance, so a drop that arrives with a real pick or capacity feed is not told it has none.
 */

/**
 * How a SKU's `faces` is arrived at, in words, for the caption under the tile.
 *
 * Now read from `DEPTH.rule`, which the build serves as `calibration.pickFace.rule` — it names the
 * divisor it actually calibrated from this drop's inventory distribution. The earlier version of
 * this comment said no structured field existed and hardcoded a divisor-free sentence; that was
 * true when written and stopped being true once the build started serving it, which is exactly how
 * a screen and its own basis statement drift apart. `DEPTH` is seeded with that sentence, so demo
 * mode and an older snapshot still read correctly. Reads as a predicate after "depth is …".
 */

/** A selection's footprint as a share of the field's own depth. Share-only on purpose: the absolute
 *  is scale-dependent, so it is never shown beside the deck's four-figure ceiling. */
function depthPct(part: number, whole: number): string {
  if (!(whole > 0)) {
    return "—";
  }
  const pct = (part / whole) * 100;
  return pct > 0 && pct < 0.1 ? "<0.1%" : pct.toFixed(1) + "%";
}

export function renderOps(ctx: LensCtx) {
  const host = need("op-grid");
  const COLS = 26;
  const ROWS = 6;
  const N = COLS * ROWS;
  if (!host.firstChild) {
    let h = "";
    for (let i = 0; i < N; i++) {
      h += `<i class="opx" style="transition-delay:${(i % COLS) * 5}ms"></i>`;
    }
    host.innerHTML = '<div class="opgrid">' + h + "</div>";
  }
  const cells = host.querySelectorAll<HTMLElement>(".opx");
  let fillFrac: number;
  let label: string;
  let cap: string;
  if (ctx.sel) {
    const f = ctx.sel.reduce((a, s) => a + s.faces, 0);
    fillFrac = ctx.baseFaces > 0 ? f / ctx.baseFaces : 0;
    label = depthPct(f, ctx.baseFaces) + " <small>of the field’s estimated pick-face depth</small>";
    // Depth is derived on every drop (no feed states boxes per face), so it is always qualified.
    cap = "Depth is " + DEPTH.rule + ".";
  } else {
    fillFrac = ctx.site.occ;
    label =
      (ctx.site.occ * 100).toFixed(0) +
      "% <small>of " +
      ctx.site.pickCap.toLocaleString() +
      " faces</small>";
    cap = simulated("capacity")
      ? "Ceiling and occupancy are planning figures · full = current occupancy."
      : "Full = current occupancy of the site’s pick faces.";
  }
  const filled = Math.round(fillFrac * N);
  cells.forEach((cell, i) => {
    const c = i % COLS;
    const r = Math.floor(i / COLS);
    const rank = c * ROWS + r;
    cell.className = "opx" + (rank < filled ? (ctx.sel ? " sel" : " on") : "");
  });
  need("op-v").innerHTML = label;
  need("op-cap").innerHTML = cap;
}

/* ===== customer — dot-grid map of the geography the site actually serves =====
 * The prototype (html ~1139) lights a dot grid wherever the cell's lon/lat falls on land, so the
 * lens reads as a map and an account bubble sits on a recognisable coast. Its own mask is a table
 * of rough continent boxes at world extent, reused verbatim below for a footprint too wide to
 * frame any tighter. UKDC's real footprint is the British Isles, where those boxes are one
 * rectangle: a coarse UK/Ireland coastline carries that frame instead. Which frame is used is
 * decided by the served markets' own positions — nothing here is pinned to this drop.
 */

export interface AccountDot {
  lon: number;
  lat: number;
  r: number;
  /** The market this dot represents — undefined for the background land-mask grid dots
   *  `drawMap` also draws with this same shape, only ever set on account bubbles. */
  name?: string;
  /** The weight (account-equivalent) this bubble's radius is scaled from — the selection's
   *  when a selection is set, the market's own site-wide weight otherwise. */
  weight?: number;
  /** The market's SITE-WIDE weight, always present alongside `weight` — lets a selection's
   *  bubble read as "X of Y accounts" even though its own radius encodes only its own share. */
  siteWeight?: number;
}

interface Box {
  lon0: number;
  lon1: number;
  lat0: number;
  lat1: number;
}

/** The prototype's world frame and its rough continent boxes, `[lonMin, latMax, lonMax, latMin]`. */
const WORLD: Box = { lon0: -168, lon1: 190, lat0: -56, lat1: 80 };
// prettier-ignore
const WLAND: readonly (readonly [number, number, number, number])[] = [
  [-168, 71, -125, 52], [-140, 60, -58, 44], [-125, 50, -64, 26], [-116, 30, -98, 16], // N America
  [-82, 10, -58, -4], [-78, 2, -40, -22], [-73, -22, -53, -52],                        // S America
  [-11, 60, 28, 45], [-9, 45, 40, 36], [4, 71, 55, 54], [24, 60, 45, 48],              // Europe
  [-17, 34, 12, 16], [-17, 16, 48, -6], [10, 8, 42, -34], [16, -6, 34, -34],           // Africa
  [26, 66, 180, 48], [38, 54, 138, 26], [58, 40, 120, 10], [95, 26, 122, 10],          // Asia
  [96, 8, 140, -9],                                                                    // SE Asia
  [113, -11, 153, -37],                                                                // Australia
];

/**
 * Coarse British Isles coastline — Great Britain, Ireland, the Isle of Man and the Channel
 * Islands, as lon/lat rings at roughly headland resolution. Deliberately crude: the dot grid is
 * ~0.8° per cell in the widget, so anything finer is thrown away by the sampling. The small isles
 * are boxes because each is one dot at that resolution, and each is a served market whose account
 * bubble would otherwise float in the sea.
 */
// prettier-ignore
const UKI: readonly (readonly (readonly [number, number])[])[] = [
  [ // Great Britain, clockwise from Land's End
    [-5.7, 50.07], [-4.1, 50.35], [-3.5, 50.62], [-2.4, 50.6], [-1.1, 50.75], [0.25, 50.77],
    [1.4, 51.1], [0.95, 51.6], [1.75, 52.5], [1.3, 52.95], [0.4, 52.9], [0.35, 53.15],
    [0.1, 53.6], [-0.1, 54.1], [-1.1, 54.6], [-1.4, 55.0], [-1.9, 55.8], [-2.6, 56.05],
    [-2.1, 57.15], [-2.0, 57.7], [-3.5, 57.85], [-3.1, 58.6], [-5.0, 58.6], [-5.2, 57.9],
    [-5.7, 57.0], [-5.5, 56.4], [-5.7, 55.4], [-4.9, 55.4], [-5.0, 54.8], [-3.6, 54.9],
    [-3.0, 54.1], [-3.1, 53.4], [-4.6, 53.35], [-4.75, 52.85], [-4.1, 52.4], [-5.3, 51.9],
    [-4.0, 51.62], [-3.0, 51.4], [-2.6, 51.3], [-4.2, 51.2], [-4.55, 50.8],
  ],
  [ // Ireland, clockwise from Cork
    [-8.3, 51.6], [-7.1, 52.1], [-6.35, 52.3], [-6.1, 53.35], [-6.2, 54.0], [-5.6, 54.6],
    [-6.0, 55.2], [-7.3, 55.35], [-8.5, 55.2], [-8.6, 54.3], [-10.1, 54.0], [-9.3, 53.3],
    [-9.9, 53.2], [-9.9, 52.55], [-10.3, 52.15], [-10.0, 51.55],
  ],
  [[-4.8, 54.05], [-4.3, 54.05], [-4.3, 54.42], [-4.8, 54.42]], // Isle of Man
  [[-2.45, 49.2], [-1.95, 49.2], [-1.95, 49.55], [-2.45, 49.55]], // Channel Islands
];

/** The coastline's own bounding box — the local frame, so the silhouette always fills it. */
const UKI_BOX: Box = {
  lon0: Math.min(...UKI.flat().map((p) => p[0])),
  lon1: Math.max(...UKI.flat().map((p) => p[0])),
  lat0: Math.min(...UKI.flat().map((p) => p[1])),
  lat1: Math.max(...UKI.flat().map((p) => p[1])),
};

function inBox(lon: number, lat: number, b: Box): boolean {
  return lon >= b.lon0 && lon <= b.lon1 && lat >= b.lat0 && lat <= b.lat1;
}

/** Even–odd ray cast. Rings are small and the grid is ~1.4k cells, so a scan is cheap enough. */
function inRing(lon: number, lat: number, ring: readonly (readonly [number, number])[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i] as readonly [number, number];
    const b = ring[j] as readonly [number, number];
    if (a[1] > lat !== b[1] > lat && lon < ((b[0] - a[0]) * (lat - a[1])) / (b[1] - a[1]) + a[0]) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * The frame the served footprint fits in: the British Isles when every market sits there, the
 * prototype's world otherwise (the demo drop ships EMEA markets, which is what it was drawn for).
 */
export function mapFrame(): {
  box: Box;
  isLand: (lon: number, lat: number) => boolean;
  fit: number | null;
} {
  return GEO_MARKETS.every((g) => inBox(g.lon, g.lat, UKI_BOX))
    ? { box: UKI_BOX, isLand: (lon, lat) => UKI.some((ring) => inRing(lon, lat, ring)), fit: 0.94 }
    : {
        box: WORLD,
        isLand: (lon, lat) =>
          WLAND.some(([a, b, c, d]) => lon >= a && lon <= c && lat <= b && lat >= d),
        fit: null,
      };
}

/**
 * Equirectangular projection of `box` onto `W × H`.
 *
 * `fit: null` stretches each axis independently, as the prototype's `projW` does — a world frame
 * is already the host's aspect. A number is an aspect-preserving fit at that fraction of the host,
 * without which a tall silhouette renders as a smear.
 */
export function projection(W: number, H: number, box: Box, fit: number | null) {
  const midLon = (box.lon0 + box.lon1) / 2;
  const midLat = (box.lat0 + box.lat1) / 2;
  // Longitude degrees shrink towards the poles; without this Britain renders squashed flat.
  const kx = Math.cos((midLat * Math.PI) / 180) || 1;
  // A single-point footprint has no span — fall back to a degree so the maths stays finite.
  const spanX = Math.max(0.5, (box.lon1 - box.lon0) * kx);
  const spanY = Math.max(0.5, box.lat1 - box.lat0);
  const k = fit === null ? null : Math.min(W / spanX, H / spanY) * fit;
  const sx = k ?? W / spanX;
  const sy = k ?? H / spanY;
  return {
    to: (lon: number, lat: number): [number, number] => [
      W / 2 + (lon - midLon) * kx * sx,
      H / 2 - (lat - midLat) * sy,
    ],
    from: (x: number, y: number): [number, number] => [
      midLon + (x - W / 2) / (kx * sx),
      midLat - (y - H / 2) / sy,
    ],
  };
}

export function drawMap(svg: SVGSVGElement, W: number, H: number, accs: AccountDot[]) {
  svg.setAttribute("viewBox", `0 0 ${W} ${H}`);
  svg.innerHTML = "";
  const frame = mapFrame();
  const proj = projection(W, H, frame.box, frame.fit);
  const cols = Math.round(W / 4.7);
  const rows = Math.round(H / 4.7);
  const dx = W / cols;
  const dy = H / rows;
  const rr = Math.min(dx, dy) * 0.34;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const [lon, lat] = proj.from(c * dx + dx / 2, r * dy + dy / 2);
      if (frame.isLand(lon, lat)) {
        svg.appendChild(
          el("circle", { cx: c * dx + dx / 2, cy: r * dy + dy / 2, r: rr, fill: "#E5E2DE" }),
        );
      }
    }
  }
  for (const a of accs) {
    const [x, y] = proj.to(a.lon, a.lat);
    if (x < -4 || x > W + 4) {
      continue;
    }
    svg.appendChild(el("circle", { cx: x, cy: y, r: a.r, fill: "#EB1700", "fill-opacity": 0.85 }));
  }
}

/** The prototype's bubble band: smallest market to largest, in px. */
const DOT_R_MIN = 2.2;
const DOT_R_MAX = 7;

/**
 * Bubble radii for one set of market weights.
 *
 * The prototype's ramp is ABSOLUTE (`2.2 + √w × 0.7`) and was tuned for weights in the tens — its
 * own markets run 3…26. Real weights are account counts running to four figures, where every
 * bubble pins to the 7px cap: measured on this drop, 13 of 14 markets came out at exactly 7.00 and
 * only the Channel Islands (21 accounts) differed, so the lens encoded no information at all.
 *
 * So: keep the prototype's curve verbatim whenever it fits inside the cap — which is every drop
 * shaped like the demo, making that path byte-identical to the prototype — and fall back to the
 * same √ curve normalised across the observed range only when the absolute one would saturate.
 * Either way the band is the prototype's 2.2…7px and the shape is its square root.
 */
function bubbleRadii(weights: readonly number[], k: number): number[] {
  const max = Math.max(0, ...weights);
  const absolute = (w: number) => DOT_R_MIN + Math.sqrt(Math.max(0, w)) * k;
  if (absolute(max) <= DOT_R_MAX) {
    return weights.map(absolute);
  }
  const span = DOT_R_MAX - DOT_R_MIN;
  return weights.map((w) => DOT_R_MIN + span * Math.sqrt(Math.max(0, w) / (max || 1)));
}

export function accountsFor(sel: readonly Sku[] | null): AccountDot[] {
  if (!sel) {
    const r = bubbleRadii(
      GEO_MARKETS.map((g) => g.weight),
      0.7,
    );
    return GEO_MARKETS.map((g, i) => ({
      lon: g.lon,
      lat: g.lat,
      r: r[i] ?? DOT_R_MIN,
      name: g.name,
      weight: g.weight,
      siteWeight: g.weight,
    }));
  }
  const geoW: Record<string, number> = {};
  for (const s of sel) {
    const per = s.accounts / Math.max(1, s.geo.length);
    for (const g of s.geo) {
      geoW[g.name] = (geoW[g.name] ?? 0) + per;
    }
  }
  const named = Object.entries(geoW).flatMap(([name, w]) => {
    const g = GEO_MARKETS.find((x) => x.name === name);
    return g ? [{ g, w }] : [];
  });
  const r = bubbleRadii(
    named.map((e) => e.w),
    0.16,
  );
  return named.map((e, i) => ({
    lon: e.g.lon,
    lat: e.g.lat,
    r: r[i] ?? DOT_R_MIN,
    name: e.g.name,
    weight: e.w,
    siteWeight: e.g.weight,
  }));
}

/**
 * How broadly a selection ships, as the median accounts per SKU.
 *
 * NOT a distinct-account total, and no longer worded as one. The payload carries an account COUNT
 * per SKU and never account identities, so a union is not computable here. This was
 * `Math.min(site.accounts, Σ per-SKU accounts)`, which double-counted every account buying more
 * than one SKU and then clamped: on the real drop the 4,868 worth-flagged SKUs summed to 9,324
 * against 5,062 served accounts, so the widget asserted the selection reached *every* account the
 * site has, under a caption promising only the accounts receiving it. `agent.ts` settled on the
 * median for the same reason; this is the same read on the visible widget.
 *
 * ponytail: median per SKU. A true distinct reach needs the build to serve account ids (or a
 * precomputed union) per material — the sales feed has them.
 */
export function medianAccounts(set: readonly Sku[]): number {
  if (!set.length) {
    return 0;
  }
  const counts = set.map((s) => s.accounts).sort((a, b) => a - b);
  return counts[counts.length >> 1] ?? 0;
}

export function renderCustomer(ctx: LensCtx) {
  need("cu-v").innerHTML = ctx.sel
    ? (() => {
        const m = medianAccounts(ctx.sel);
        return m > 0
          ? m.toLocaleString() + " <small>accounts / SKU, median</small>"
          : "0 <small>— over half ship to no accounts</small>";
      })()
    : ctx.site.accounts.toLocaleString() + " <small>accounts</small>";
  drawMap(need<SVGSVGElement>("cu-svg"), 260, 120, accountsFor(ctx.sel));
  // The footprint is whatever geography the data carries — never a hardcoded region name.
  need("cu-cap").textContent = ctx.sel
    ? "How broadly the selection ships. Accounts overlap between SKUs, so this is per SKU, not a total."
    : `All accounts this site serves, across ${GEO_MARKETS.length} markets.`;
}

/* ============ the SKUs under one cluster glyph ============ */

/**
 * How many members of a stack the panel lists. The heaviest cell on a real drop holds thousands,
 * so this is a legibility cap, not a sample: the row count and the true total are both printed,
 * on the same "Showing N of M" pattern the report pages use.
 */
const CLUSTER_ROWS = 12;

/** The node colours, as the painter assigns them — same expressions as the modal's tag list. */
function dotColour(s: Sku): string {
  return s.worth ? "#EB1700" : s.premiumSlow ? "#6d8a6f" : s.isNew ? NPI_TINT : "#A39992";
}

/** The flags a decision reads, in the same words the SKU profile's chip uses. */
function flagWords(s: Sku): string {
  const f: string[] = [];
  if (s.worth) {
    f.push(s.type === "Dx" ? "slow · overstocked" : "slow · low-value");
  }
  if (s.premiumSlow) {
    f.push("slow · premium");
  }
  if (s.isNew) {
    f.push("NPI · protected");
  }
  // No "slot mismatch" flag: it needed a current-slot feed no drop carries, and was simulated.
  // `pickReco` is a recommendation, printed on the profile rather than flagged as a problem.
  return f.join(" · ");
}

/**
 * The constituent list for a cluster click: every SKU stacked at that one screen position, in the
 * order the plot handed them (worth → premium → NPI → plain, its own paint priority).
 *
 * Nothing is aggregated or re-sorted here — each cell is that SKU's own served field, so a row
 * reconciles with its profile. `pick.mix` is the painter's count of the whole stack, which is why
 * the breakdown adds up to the total rather than to the twelve rows shown.
 */
function clusterList(pick: ClusterPick, sel: readonly Sku[], site: Site): string {
  const byId = new Map(sel.map((s) => [s.id, s]));
  const rows = pick.ids
    .slice(0, CLUSTER_ROWS)
    .map((id) => byId.get(id))
    .filter((s): s is Sku => s !== undefined);
  if (!rows.length) {
    return "";
  }
  const cur = curSym(site.ntsCurrency);
  const m = pick.mix;
  const mix = [
    [m.worth, "slow &amp; low-value"],
    [m.premiumSlow, "slow but premium"],
    [m.npi, "NPI"],
    [m.plain, "healthy movers"],
  ] as const;
  const breakdown = mix
    .filter(([n]) => n > 0)
    .map(([n, label]) => `<b>${n.toLocaleString()}</b> ${label}`)
    .join(" · ");
  return `<div class="rp-eyebrow" style="margin-top:18px"><span class="eyebrow">At this mark</span><span class="eyebrow">${pick.ids.length.toLocaleString()} SKUs stacked</span></div>
  <div class="rp-hint" style="margin-top:0">${breakdown} · ${m.rx.toLocaleString()} Rx / ${m.dx.toLocaleString()} Dx. One mark, one velocity and value — the variants below share it.</div>
  ${rows
    .map((s) => {
      const flags = flagWords(s);
      return `<div class="tagrow" data-sku="${s.id}" style="cursor:pointer;align-items:flex-start">
      <span class="tn"><span class="dot" style="background:${dotColour(s)}"></span>${s.name}${flags ? `<br><small style="font-size:10px;color:var(--w6);margin-left:13px">${flags}</small>` : ""}</span>
      <span class="tv" style="text-align:right;white-space:nowrap">${fmtVel(s.vel)}/mo · ${Math.round(s.inv).toLocaleString()} bx<br><small style="font-size:10px">${s.type === "Dx" ? cur + "0" : moneyM(s.value, site.ntsCurrency)}</small></span>
    </div>`;
    })
    .join("")}
  <div class="rp-hint">Showing ${rows.length.toLocaleString()} of ${pick.ids.length.toLocaleString()}, most decision-relevant first · velocity in boxes/month, stock in boxes held, value ${MONTH_SLOTS.length >= 12 ? "over the months observed" : "annualised"} · click a row for that SKU's profile.</div>`;
}

/* ============ right panel ============ */

/**
 * The `#rightp` read-out: whole-site anchors with nothing selected, one SKU's profile for a
 * single click, cumulative revenue/capacity share for a marquee.
 */
export function renderRight(ctx: LensCtx): string {
  const { site } = ctx;
  const sel = ctx.sel;
  const cur = curSym(site.ntsCurrency);
  /** Per box, and real prices are fractional. */
  const price = (p: number) => cur + p.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (!sel) {
    // One pass over the field: the cohort count plus the NPI share of the non-movers, which
    // has to be stated or a five-figure non-mover count reads as that many dead SKUs.
    let slow = 0;
    let dead = 0;
    let deadNpi = 0;
    for (const s of ctx.skus) {
      if (s.worth) {
        slow++;
      }
      if (s.lastMovedQ >= 1) {
        dead++;
        if (s.isNew) {
          deadNpi++;
        }
      }
    }
    const nts = ntsRead(site);
    const ntsCell = nts.total
      ? `${nts.top}<br><small style="font-size:10.5px;color:var(--w7);font-family:var(--text)">${nts.total}</small>`
      : nts.top;
    // No year in the eyebrow: the window is the drop's own (a single fiscal month here), and
    // the rail's provenance caption is the one place that names it.
    //
    // "Held" and "shipped" are two feeds on two dates — the stock position is months after the
    // sales window on the current drop — so each row carries its own basis and the note states
    // the gap. One undated pair here is what let the screen imply a single period.
    const asAt = BASIS.inventoryAsOf
      ? ` <small style="font-size:10px;color:var(--w6);font-weight:400">as at ${BASIS.inventoryAsOf}</small>`
      : "";
    const gapNote = BASIS.inventoryAsOf
      ? ` <b>Held</b> is the stock position as at ${BASIS.inventoryAsOf}${BASIS.gap ? `, ${BASIS.gap} the sales window (${BASIS.salesSpan})` : ""} — the two rows above are different dates, so a SKU can be held here and have shipped outside the window.`
      : ` No dated stock position accompanies this data, so <b>held</b> cannot be dated against the sales window (${BASIS.salesSpan}).`;
    return `<div class="rp-eyebrow"><span class="eyebrow">Site overview</span><span class="eyebrow">whole site</span></div>
    <div class="rp-title">UKDC</div><div class="rp-sub">Whole-site view · select SKUs to go deeper</div>
    <div class="kv"><span class="k">SKUs held${asAt}</span><span class="v">${site.heldSKUs.toLocaleString()}</span></div>
    <div class="kv"><span class="k">SKUs shipped <small style="font-size:10px;color:var(--w6);font-weight:400">${BASIS.salesSpan}</small></span><span class="v">${site.shippedSKUs.toLocaleString()}</span></div>
    <div class="kv"><span class="k">Net trade sales</span><span class="v" style="text-align:right">${ntsCell}</span></div>
    <div class="kv"><span class="k">Accounts served</span><span class="v">${site.accounts.toLocaleString()}</span></div>
    <div class="kv"><span class="k">Held, not shipped</span><span class="v" style="color:var(--red-ink)">${site.deadCount.toLocaleString()}</span></div>
    <div class="rp-note"><b>${slow.toLocaleString()} SKUs</b> in view are a poor return on space (red) — slow low-value Rx, and overstocked slow-moving Dx — yet still hold pick faces. The candidates to relocate, convert or de-list.${deadNpi ? ` Of the <b>${dead.toLocaleString()}</b> that did not ship in the window, <b>${deadNpi.toLocaleString()}</b> (${Math.round((deadNpi / dead) * 100)}%) are NPI-flagged — newly launched rather than dead, and protected from every lever.` : ""} Open <b>Capacity scenarios</b> to act on them.${gapNote}</div>
    <div class="rp-hint">Tip · drag a box around a cluster to see its cumulative share of revenue &amp; capacity. A single mark can hold many SKUs sharing a velocity and value, so clicking one takes the whole stack — zoom in to separate them.</div>`;
  }
  const first = sel[0];
  if (sel.length === 1 && first) {
    const s = first;
    // The launch date is simulated on every drop so far, and this sub-line is the most-read
    // per-SKU claim on the screen. Marked off the drop's own provenance, like the face estimate:
    // the day a launch-date feed arrives the marker disappears by itself.
    const launchMark = simulated("launch") ? " (est.)" : "";
    const chip = s.worth ? "slow" : s.premiumSlow ? "prem" : "fast";
    const chipT = s.worth
      ? s.type === "Dx"
        ? s.band + " · slow · overstocked"
        : s.band + " · slow & low-value"
      : s.premiumSlow
        ? s.band + " · slow but premium"
        : s.band === "A"
          ? "A · fast mover"
          : s.band === "B"
            ? "B · steady"
            : s.band + " · slow";
    return `<div class="rp-eyebrow"><span class="eyebrow">SKU profile</span><button data-clear="1" style="border:none;background:none;color:var(--w7);font-size:11px;cursor:pointer">clear ✕</button></div>
    <div class="rp-title">${s.name}</div><div class="rp-sub">${s.type} · ${s.type === "Dx" ? "fitting set" : price(s.price) + "/box"} · launched ${s.launched}${launchMark}</div>
    <div class="rp-chip ${chip}">${chipT}</div>
    <div class="kv"><span class="k">Velocity</span><span class="v">${fmtVel(s.vel)}<small style="font-size:11px;color:var(--w7)"> boxes/mo</small></span></div>
    <div class="kv"><span class="k">Annual value</span><span class="v">${s.type === "Dx" ? cur + "0" : moneyM(s.value, site.ntsCurrency)}</span></div>
    <div class="kv"><span class="k">Inventory held</span><span class="v">${Math.round(s.inv).toLocaleString()}<small style="font-size:11px;color:var(--w7)"> boxes</small></span></div>
    <div class="kv"><span class="k">Pick faces <small style="font-size:10px;color:var(--w6)">est.</small></span><span class="v">≈ ${s.faces}</span></div>
    <div class="kv"><span class="k">Suits <small style="font-size:10px;color:var(--w6)">reco.</small></span><span class="v">${s.pickReco}</span></div>
    ${
      s.worth
        ? s.type === "Dx"
          ? `<div class="rp-note"><b>Worth talking about.</b> ${Math.round(s.inv).toLocaleString()} boxes and ≈${s.faces} face-equivalents of depth tied up in a fitting SKU shipping just ${fmtVel(s.vel)}/mo — overstocked dormant stock. A candidate to thin, convert to on-demand, or de-list.</div>`
          : `<div class="rp-note"><b>Worth talking about.</b> ${Math.round(s.inv).toLocaleString()} boxes and ≈${s.faces} face-equivalents of depth tied up for ${moneyM(s.value, site.ntsCurrency)} a year — poor return on space. A candidate to thin or relocate.</div>`
        : s.premiumSlow
          ? `<div class="rp-note calm"><b>Slow but valuable.</b> Low velocity, but at ${price(s.price)}/box it still earns ${moneyM(s.value, site.ntsCurrency)} a year. Keep — velocity alone would mislead here.</div>`
          : `<div class="rp-note calm">Healthy mover — earning its place at UKDC.</div>`
    }`;
  }
  const tot = sel.reduce((a, s) => a + s.value, 0);
  const faces = sel.reduce((a, s) => a + s.faces, 0);
  const slowSel = sel.filter((s) => s.worth);
  // Both figures are the normalised reporting currency, so the share is a like-for-like ratio.
  const sharePct = (tot / site.nts) * 100;
  return `<div class="rp-eyebrow"><span class="eyebrow">${sel.length.toLocaleString()} SKUs selected</span><button data-clear="1" style="border:none;background:none;color:var(--w7);font-size:11px;cursor:pointer">clear ✕</button></div>
  <div class="rp-title">Selection</div><div class="rp-sub">Aggregated across ${sel.length.toLocaleString()} SKUs</div>
  <div class="kv"><span class="k">Combined value</span><span class="v">${moneyM(tot, site.ntsCurrency)}</span></div>
  <div class="kv"><span class="k">Share of site NTS</span><span class="v">${sharePct.toFixed(1)}%</span></div>
  <div class="kv"><span class="k">Pick-face depth <small style="font-size:10px;color:var(--w6)">est.</small></span><span class="v">≈ ${faces.toLocaleString()}</span></div>
  <div class="kv"><span class="k">Slow &amp; low-value</span><span class="v" style="color:var(--red-ink)">${slowSel.length.toLocaleString()}</span></div>
  <div class="rp-note"><b>${sel.length.toLocaleString()} SKUs</b> = <b>${sharePct.toFixed(1)}%</b> of site revenue on <b>≈ ${faces.toLocaleString()} face-equivalents</b> of pick-face depth — a footprint, not the site’s planning faces. ${slowSel.length ? `Freeing the ${slowSel.length.toLocaleString()} slow ones opens <b>≈ ${slowSel.reduce((a, s) => a + s.faces, 0).toLocaleString()} of that depth</b>.` : ""}</div>
  ${ctx.pick ? clusterList(ctx.pick, sel, site) : ""}`;
}

/* ============ expand modal ============ */

/**
 * Where a destination's label goes.
 *
 * A ribbon's own centre, unless the ribbon above it left too little room: a 0%-of-flow channel is a
 * 3px band, so six of them stack inside one label's height and the text collides (which is exactly
 * how it was reported). The bars keep their true heights — the label is what gets pushed, in the
 * same order as the bands, so it still reads down the column.
 */
export function labelStack(pitch: number) {
  let last = -Infinity;
  return (centre: number) => (last = Math.max(centre, last + pitch));
}

export function drawSankeyBig(svg: SVGSVGElement, dests: Dest[], hasSel: boolean, mode: string) {
  const W = 880;
  const H = 420;
  svg.setAttribute("viewBox", `0 0 ${W} ${H}`);
  svg.innerHTML = "";
  const COLX = [44, 250, 460, 650];
  const NW = 14;
  const top = 44;
  const usable = H - 84;
  const tot = dests.reduce((a, d) => a + d.siteV, 0) || 1;
  // Every label here is either served or names the stage without naming a place. The upstream
  // plant is deliberately unnamed: no served field identifies a supplying plant, and this column
  // previously carried a hardcoded site name that appeared nowhere in the payload.
  [
    "PLANT · not in this drop",
    "REGIONAL DC · UKDC",
    `IN-MARKET · ${GEO_MARKETS.length} MARKETS`,
    mode === "region" ? "MARKET · REGION" : "CHANNEL · CUSTOMER",
  ].forEach((t, i) => {
    svg.appendChild(
      svgText(
        { x: COLX[i] ?? 0, y: 26, "font-size": 10, fill: "#81766F", "letter-spacing": ".04em" },
        t,
      ),
    );
  });
  const [c0 = 0, c1 = 0, c2 = 0, c3 = 0] = COLX;
  for (const x of [c0, c1, c2]) {
    svg.appendChild(el("rect", { x, y: top, width: NW, height: usable, rx: 2, fill: "#141414" }));
  }
  svg.appendChild(
    el("path", {
      d: ribbon(c0 + NW, top, top + usable, c1, top, top + usable),
      fill: "#C1BBB3",
      "fill-opacity": 0.3,
    }),
  );
  svg.appendChild(
    el("path", {
      d: ribbon(c1 + NW, top, top + usable, c2, top, top + usable),
      fill: "#C1BBB3",
      "fill-opacity": 0.3,
    }),
  );
  if (hasSel) {
    const selTot = dests.reduce((a, d) => a + d.selV, 0);
    const rh = (selTot / tot) * usable;
    svg.appendChild(
      el("path", {
        d: ribbon(c0 + NW, top + usable - rh, top + usable, c1, top + usable - rh, top + usable),
        fill: "#EB1700",
        "fill-opacity": 0.8,
      }),
    );
    svg.appendChild(
      el("path", {
        d: ribbon(c1 + NW, top + usable - rh, top + usable, c2, top + usable - rh, top + usable),
        fill: "#EB1700",
        "fill-opacity": 0.8,
      }),
    );
  }
  const order = dests.slice().sort((a, b) => b.siteV - a.siteV);
  const gap = 12;
  // Length from the destination list, so any channel or market count lays out.
  const avail = usable - gap * Math.max(1, dests.length - 1);
  // Two stacked text rows per destination, so one label block is ~24px tall.
  const labY = labelStack(26);
  let ty = top;
  let sy = top;
  for (const d of order) {
    const v = d.siteV;
    const srcH = (v / tot) * usable;
    const dstH = Math.max(3, (v / tot) * avail);
    svg.appendChild(
      el("path", {
        d: ribbon(c2 + NW, sy, sy + srcH, c3, ty, ty + dstH),
        fill: d.color,
        "fill-opacity": hasSel ? 0.16 : 0.42,
      }),
    );
    const rS = (d.selV / tot) * usable;
    const rD = (d.selV / tot) * avail;
    if (hasSel && rD > 0.4) {
      svg.appendChild(
        el("path", {
          d: ribbon(c2 + NW, sy + srcH - rS, sy + srcH, c3, ty + dstH - rD, ty + dstH),
          fill: "#EB1700",
          "fill-opacity": 0.82,
        }),
      );
    }
    svg.appendChild(
      el("rect", {
        x: c3,
        y: ty,
        width: NW,
        height: dstH,
        rx: 2,
        fill: d.color,
        "fill-opacity": hasSel ? 0.3 : 0.9,
      }),
    );
    if (hasSel && rD > 0.4) {
      svg.appendChild(
        el("rect", { x: c3, y: ty + dstH - rD, width: NW, height: rD, rx: 2, fill: "#EB1700" }),
      );
    }
    const ly = labY(ty + dstH / 2 - 1);
    svg.appendChild(
      svgText(
        {
          x: c3 + NW + 8,
          y: ly,
          "font-size": 11,
          "font-weight": 600,
          fill: "#141414",
        },
        d.label,
      ),
    );
    svg.appendChild(
      svgText(
        { x: c3 + NW + 8, y: ly + 12, "font-size": 10, fill: "#81766F" },
        Math.round((v / tot) * 100) + "% · " + Math.round(v).toLocaleString() + " u",
      ),
    );
    sy += srcH;
    ty += dstH + gap;
  }
}

/** Fills the expand sheet for one widget. Title/eyebrow are rendered by React. */
export function renderModal(widget: WidgetKey, ctx: LensCtx) {
  const sel = ctx.sel;
  const set = sel ?? ctx.view;
  const svg = need<SVGSVGElement>("m-svg");
  const html = need("m-html");
  svg.style.display = "none";
  html.style.display = "none";
  svg.innerHTML = "";
  html.innerHTML = "";
  const mv = need("m-v");
  const mcap = need("m-cap");
  // side: tagged SKUs
  const tags = [...set].sort((a, b) => b.value - a.value).slice(0, 14);
  need("m-side-t").textContent = sel ? sel.length + " selected SKUs" : "Top SKUs in view";
  need("m-tags").innerHTML = tags
    .map(
      (s) =>
        `<div class="tagrow"><span class="tn"><span class="dot" style="background:${s.worth ? "#EB1700" : s.premiumSlow ? "#6d8a6f" : "#A39992"}"></span>${s.name}</span><span class="tv">${s.type === "Dx" ? curSym(ctx.site.ntsCurrency) + "0" : moneyM(s.value, ctx.site.ntsCurrency)}</span></div>`,
    )
    .join("");

  if (widget === "channel") {
    svg.style.display = "block";
    // The same destinations the compact widget draws, so the Channel/Region toggle in the sheet
    // header reads through here instead of the expanded view silently reverting to channels.
    const dests = chanDests(ctx);
    drawSankeyBig(svg, dests, sel !== null, ctx.chanMode);
    const units = dests.reduce((a, d) => a + (sel ? d.selV : d.siteV), 0);
    mv.textContent = Math.round(units).toLocaleString() + " boxes/mo";
    mcap.textContent =
      (ctx.chanMode === "region"
        ? "Plant → regional DC → in-market DC → market. "
        : "Plant → regional DC → in-market DC → channel. ") +
      (sel ? "Red = the selection’s share of the flow." : "Whole-site flow.");
    return;
  }
  if (widget === "business") {
    html.style.display = "block";
    html.innerHTML = '<div class="nts" id="m-nts" style="height:78%"></div>';
    const { mon: siteMon, val: siteVal } = ntsData(ctx.view);
    const selected = sel ? ntsData(sel) : null;
    const host = html.querySelector<HTMLElement>(".nts");
    if (host) {
      drawNTS(host, siteMon, selected ? selected.mon : null);
    }
    const nts = ntsRead(ctx.site);
    mv.textContent = selected
      ? fmtMoneyShort(selected.val * (ctx.site.nts / siteVal), ctx.site.ntsCurrency) +
        " · " +
        ((selected.val / siteVal) * 100).toFixed(1) +
        "%"
      : nts.top;
    mcap.textContent = selected
      ? `${shareCaption()} The ABC read: few SKUs, most of the revenue.`
      : (nts.total ? nts.total + " · " : "") + periodCaption(siteMon.length);
    return;
  }
  if (widget === "ops") {
    html.style.display = "block";
    const selFaces = sel ? sel.reduce((a, s) => a + s.faces, 0) : 0;
    const share = sel ? selFaces / ctx.baseFaces : 0;
    const occ = Math.round(ctx.site.occ * 100);
    const over = Math.round((ctx.site.occ + ctx.site.npiOver) * 100);
    // The bar's axis is the deck ceiling, so a selection fills its share OF the occupied band —
    // never its share of the estimated faces plotted against a ceiling it isn't measured in.
    const fillPct = sel ? share * occ : occ;
    /**
     * The bar's box is the whole axis, and the NPI overshoot runs PAST the ceiling — so the axis
     * has to end at the overshoot, not at 100%. It ended at 100% and the hatched band was drawn
     * `width: over - occ` from `left: occ`, which put its right edge at `over`% of the box: on this
     * drop (94% occupied, +10% NPI) it ran 4% of the bar's width out over the side panel.
     */
    const axisMax = Math.max(100, over);
    const ax = (v: number) => (v / axisMax) * 100;
    // The two ticks land ~6% apart here, so their labels sit on opposite sides of the bar rather
    // than on one baseline where they overlap. A label near the right end hangs left of its tick,
    // or it overflows the box on a drop with no overshoot (ceiling at the very edge). The overshoot
    // itself gets no label — the sentence under the bar already names it, and a third label on this
    // little space is what the collision was made of.
    const edge = (v: number) => (ax(v) > 92 ? "transform:translateX(-100%)" : "");
    html.innerHTML = `<div style="padding:16px 0">
      <div style="font-size:13px;color:var(--w7);margin-bottom:26px">UKDC pick-face capacity · <b style="color:#141414">${ctx.site.pickCap.toLocaleString()} faces</b> <small>(planning figure)</small></div>
      <div class="hbar big"><div class="hbar-fill" style="width:${ax(fillPct)}%"></div><div class="hbar-over" style="left:${ax(occ)}%;width:${ax(over) - ax(occ)}%"></div>
        <div class="hbar-tick r" style="left:${ax(100)}%"></div><div class="hbar-tlab top" style="left:${ax(100)}%;color:#9E0000;${edge(100)}">ceiling 100%</div>
        <div class="hbar-tick" style="left:${ax(occ)}%"></div><div class="hbar-tlab" style="left:${ax(occ)}%;${edge(occ)}">occupied ${occ}%</div></div>
      <div style="font-size:13px;line-height:1.6;color:var(--w8);margin-top:30px">${sel ? `The selection holds <b style="color:#9E0000">≈ ${Math.round(selFaces).toLocaleString()} face-equivalents</b> — <b>${(share * 100).toFixed(1)}%</b> of the site's ${Math.round(ctx.baseFaces).toLocaleString()}. Relocating it frees that share of the pick face. Faces are estimated from boxes held; no pick-location feed exists in this data, and the ceiling and occupancy above are planning figures on their own basis.` : `Occupancy is <b>${occ}%</b> today; the hatched band past 100% is the NPI pipeline pushing <b style="color:#9E0000">over the ceiling to ${over}%</b>. Both are planning figures — this site has no capacity feed.`}</div></div>`;
    mv.textContent = sel
      ? "≈ " + Math.round(selFaces).toLocaleString() + " faces"
      : `${occ}% of ${ctx.site.pickCap.toLocaleString()}`;
    mcap.textContent = "";
    return;
  }
  svg.style.display = "block";
  drawMap(svg, 760, 420, accountsFor(sel));
  // Same read as the compact widget: per-SKU median, never a clamped sum of overlapping sets.
  mv.textContent = sel
    ? `${medianAccounts(set).toLocaleString()} accounts / SKU, median`
    : `${ctx.site.accounts.toLocaleString()} accounts`;
  mcap.textContent = sel
    ? "How broadly the selection ships — per SKU, since accounts overlap between SKUs. Relocation changes supply to these markets."
    : "All accounts served from UKDC.";
}
